/**
 * WHC Lab (Newcastle, Co. Wicklow, Ireland) — dry and liquid brewing cultures.
 *
 * Every figure here was read off WHC's own product pages at www.whclab.com in
 * September 2026, and off the maker-hosted Technical Data Sheet PDFs those
 * pages link to where a page left a field out. WHC publish in °C already, so
 * nothing is converted; they publish dosage in g/hL for some products and g/L
 * for others, and the g/L figures are multiplied by 100 into this file's
 * `dosageGramsPerHl`.
 *
 * Flocculation is published as a two-step band ("Medium to High", "Low to
 * Medium"). Where the five-step vocabulary has a rung between the two named
 * steps that rung is used; where it does not ("Medium Low to Medium") the
 * lower of the two is used, because over-stating how hard a strain drops is
 * the error a brewer pays for.
 *
 * Marked `representative`, with the reason in each description:
 *
 * - `whc-farmhouse-vibes` — WHC print attenuation as "80%+", so the upper
 *   bound here is ours.
 * - `whc-thiol-unleashed`, `whc-zero-hero` — no flocculation published.
 * - `whc-big-apple`, `whc-surge-seltzer` — neither attenuation nor
 *   flocculation published.
 * - `whc-hyperdrive` — no attenuation published.
 * - `whc-lactobacillus-plantarum`, `whc-lactobacillus-helveticus` — pure
 *   bacteria pitches; the attenuation band is nominal, as on Escarpment's
 *   Lactobacillus Blend 2.0.
 *
 * STA1 is `null` throughout bar two: WHC market Farmhouse Vibes as a diastatic
 * saison strain, and say in as many words that Banana Split is *not* diastatic.
 *
 * Two things are deliberately absent. WHC's distilling range (Hades,
 * Prometheus, Poseidon, Apollo, Ares, Hercules) publishes neither attenuation
 * nor flocculation and is a spirits line rather than a brewing one. And the
 * "Old Faithful" and "Bonfire" names do not appear anywhere in WHC's current
 * catalogue — not in `/products.json`, not in any collection — so nothing was
 * entered for them rather than guessing at a discontinued product's numbers.
 */

import type { Yeast } from '../../schema/yeast';

/** WHC Lab cultures. @see Yeast */
export const whcYeasts: readonly Yeast[] = [
  // -------------------------------------------------------------------------
  // Dry — hop-forward ales
  // -------------------------------------------------------------------------
  {
    id: 'whc-saturated',
    lab: 'WHC Lab',
    productCode: 'Saturated',
    name: 'Saturated',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      "WHC's hazy-IPA workhorse and the reason most breweries know the name. It stops short of dry, leaving body and residual sweetness behind, and throws stone-fruit esters — mango, peach, a little vanilla — that sit on top of a modern dry hop rather than under it. Haze holds well in package.",
    tags: ['hazy', 'neipa', 'ipa', 'stone-fruit', 'biotransformation', 'full-body'],
    url: 'https://www.whclab.com/products/saturated-fruity-new-england-ipa-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-lax',
    lab: 'WHC Lab',
    productCode: 'LAX',
    name: 'LAX',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'The clean side of the WHC range: a neutral West Coast strain that gets out of the way so the hop bill reads unobstructed. Attenuates firmly, tolerates gravity, and suits pale ales, hoppy lagers-by-another-name and big stouts equally.',
    tags: ['west-coast', 'ipa', 'neutral', 'clean', 'pale-ale', 'high-gravity'],
    url: 'https://www.whclab.com/products/lax-clean-west-coast-ipa-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-haze-heaven',
    lab: 'WHC Lab',
    productCode: 'Haze Heaven',
    name: 'Haze Heaven',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'An English-derived strain aimed squarely at hop aroma: tropical fruit and citrus lifted out of the dry hop, a soft mouthfeel and moderate attenuation that leaves the beer with medium body. It reads as a cask ale strain at the cool end and a hazy IPA strain warm.',
    tags: ['hazy', 'neipa', 'english-ale', 'ipa', 'citrus', 'tropical'],
    url: 'https://www.whclab.com/products/haze-heaven-neipa-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-pineapple-passion',
    lab: 'WHC Lab',
    productCode: 'Pineapple Passion',
    name: 'Pineapple Passion',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 22, max: 26 },
    flocculation: 'medium',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Selected for the pineapple note it throws on its own, before any hop is counted. Ferments warm and fast with heavy biotransformation and good haze stability, so it suits a juicy IPA where the yeast is meant to be part of the fruit rather than a neutral base.',
    tags: ['tropical', 'pineapple', 'ipa', 'hazy', 'biotransformation', 'warm'],
    url: 'https://www.whclab.com/products/pineapple-passion-dry-ipa-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-thiol-unleashed',
    lab: 'WHC Lab',
    productCode: 'Thiol Unleashed',
    name: 'Thiol Unleashed',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 74, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'A non-GMO hybrid bred to cleave bound thiols out of malt and hops during fermentation, pushing passionfruit, guava and grapefruit well beyond what the hop bill alone would give. WHC publish no flocculation grade for it, so that field is class-typical rather than theirs.',
    tags: ['thiol', 'tropical', 'passionfruit', 'ipa', 'hazy', 'aroma'],
    url: 'https://www.whclab.com/products/thiol-unleashed-hybrid-non-gmo-dried-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Dry — thermotolerant and kveik
  // -------------------------------------------------------------------------
  {
    id: 'whc-high-voltage',
    lab: 'WHC Lab',
    productCode: 'High Voltage',
    name: 'High Voltage',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 31, max: 35 },
    flocculation: 'high',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'A thermotolerant strain sold as a single-pitch answer to nearly any style, lager included: it works in the low thirties, which cuts both tank time and the glycol bill. Hard flocculating and tolerant to 17 %, so it also handles seltzer bases and imperial stouts.',
    tags: ['thermotolerant', 'clean', 'fast', 'high-gravity', 'seltzer', 'versatile'],
    url: 'https://www.whclab.com/products/high-voltage-thermotolerant-dry-brewing-yeast',
    dosageGramsPerHl: { min: 30, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-mango-madness',
    lab: 'WHC Lab',
    productCode: 'Mango Madness',
    name: 'Mango Madness',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 31, max: 37 },
    flocculation: 'low',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'The fruity half of the thermotolerant pair: guava and mango aroma, heavy biotransformation, and a turnaround fast enough to skip a diacetyl rest. WHC note that it dry-hops happily at 15–25 °C and creeps less than most IPA strains.',
    tags: ['thermotolerant', 'mango', 'tropical', 'ipa', 'fast', 'biotransformation'],
    url: 'https://www.whclab.com/products/mango-madness-thermotolerant-ipa-dry-brewing-yeast',
    dosageGramsPerHl: { min: 30, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-voss-kveik',
    lab: 'WHC Lab',
    productCode: 'Voss Kveik',
    name: 'Voss Kveik',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 30, max: 40 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The Voss farmhouse culture, dried. Run it at 40 °C for the orange-citrus ester bloom kveik is known for, or hold it near 30 °C for something much closer to a clean ale. Fast, gravity-tolerant and forgiving of a brewery with no chilling to spare.',
    tags: ['kveik', 'voss', 'norwegian', 'citrus', 'orange', 'fast', 'thermotolerant'],
    url: 'https://www.whclab.com/products/voss-kveik-dried-brewing-yeast',
    dosageGramsPerHl: { min: 30, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-hornindal-kveik',
    lab: 'WHC Lab',
    productCode: 'Hornindal Kveik',
    name: 'Hornindal Kveik',
    form: 'dry',
    type: 'kveik',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 28, max: 37 },
    flocculation: 'high',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'The Hornindal culture, dried, and the tropical one of the two kveiks WHC sell: tangerine, pineapple and mango that get louder the warmer it runs. Flocculates hard at the top of its range, which shortens conditioning as well as fermentation.',
    tags: ['kveik', 'hornindal', 'norwegian', 'tropical', 'pineapple', 'thermotolerant'],
    url: 'https://www.whclab.com/products/hornindal-kveik-ipa-dry-brewing-yeast',
    dosageGramsPerHl: { min: 30, max: 100 },
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // Dry — British, Belgian and wheat
  // -------------------------------------------------------------------------
  {
    id: 'whc-british-pub-ale',
    lab: 'WHC Lab',
    productCode: 'British Pub Ale',
    name: 'British Pub Ale',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'high',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'WHC pitch this as a house strain rather than a style strain: a hard-flocculating English ale yeast that carries bitters, stouts and barleywines without complaint and drops the beer bright on its own. Tolerant to 14 %, so it takes a big beer too.',
    tags: ['english-ale', 'house-strain', 'bitter', 'stout', 'clearing', 'workhorse'],
    url: 'https://www.whclab.com/products/british-pub-ale-dry-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-old-english',
    lab: 'WHC Lab',
    productCode: 'Old English',
    name: 'Old English',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 78, max: 85 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'A Burton heritage strain and the driest finisher in the WHC ale range — up to 85 % apparent — with ester production kept low, so the malt and hops carry the beer rather than the yeast. Fast and gravity-tolerant, which makes it a sensible pick for strong ales.',
    tags: ['english-ale', 'burton', 'dry', 'high-attenuation', 'porter', 'high-gravity'],
    url: 'https://www.whclab.com/products/old-english-ale-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-fountainbridge',
    lab: 'WHC Lab',
    productCode: 'Fountainbridge',
    name: 'Fountainbridge',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A recent addition aimed at cask: middling attenuation, good flocculation and a profile that stays balanced across the whole of its temperature band. WHC position it as the adaptable one — pale ales and West Coast IPAs as readily as a traditional cask bitter.',
    tags: ['cask-ale', 'english-ale', 'pale-ale', 'balanced', 'clearing'],
    url: 'https://www.whclab.com/products/fountainbridge-ale-dry-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-the-dubliner',
    lab: 'WHC Lab',
    productCode: 'The Dubliner',
    name: 'The Dubliner',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 75, max: 80 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 15,
    sta1: null,
    description:
      'Built for the Irish end of the shelf: stout, porter, red ale and cask. It handles dark, roast-heavy wort well and finishes crisp and dry rather than sweet, and at 15 % tolerance it takes an imperial stout without a second pitch.',
    tags: ['irish', 'stout', 'porter', 'red-ale', 'cask-ale', 'dry', 'high-gravity'],
    url: 'https://www.whclab.com/products/the-dubliner-irish-stout-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-banana-split',
    lab: 'WHC Lab',
    productCode: 'Banana Split',
    name: 'Banana Split',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 19, max: 24 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: false,
    description:
      'A Weihenstephan-derived hefeweizen strain that leans hard on isoamyl acetate — banana first, with apple, clove and plum underneath. Push the temperature to push the banana. WHC state plainly that it is not diastatic, unlike several wheat strains sold alongside it.',
    tags: ['hefeweizen', 'wheat', 'banana', 'clove', 'weihenstephan', 'esters'],
    url: 'https://www.whclab.com/products/banana-split-hefeweizen-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-belgian-haze',
    lab: 'WHC Lab',
    productCode: 'Belgian Haze',
    name: 'Belgian Haze',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 10,
    sta1: null,
    description:
      'A dried wit strain: clove and pepper phenolics, fruit esters and a gentle rounded acidity, stopping short of dry so the beer keeps its body. Its 10 % tolerance marks it out as a witbier and Belgian pale yeast rather than a strong-ale one.',
    tags: ['belgian', 'witbier', 'phenolic', 'clove', 'pepper', 'hazy'],
    url: 'https://www.whclab.com/products/belgian-haze-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-farmhouse-vibes',
    lab: 'WHC Lab',
    productCode: 'Farmhouse Vibes',
    name: 'Farmhouse Vibes',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 80, max: 90 },
    temperatureC: { min: 19, max: 26 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 14,
    sta1: true,
    description:
      'A diastatic saison strain: bubblegum, pepper and fruit over a glycerol-rich body, running dry and warm. Being diastatic it keeps eating dextrins long after gravity looks stable, so package late or not at all in glass. WHC print attenuation only as "80 %+"; the upper bound here is ours.',
    tags: ['saison', 'belgian', 'diastatic', 'sta1', 'phenolic', 'pepper', 'dry'],
    url: 'https://www.whclab.com/products/farmhouse-vibes-saison-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 150 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Dry — lager and lager-like
  // -------------------------------------------------------------------------
  {
    id: 'whc-einstein',
    lab: 'WHC Lab',
    productCode: 'Einstein',
    name: 'Einstein',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 9, max: 16 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      "WHC's Weihenstephan-origin bottom-fermenting lager strain, dried. Clean and crisp across pils, helles and bock, with a low nitrogen demand and a genuinely cold working range. Low flocculation, so give it filtration or time.",
    tags: ['lager', 'pilsner', 'helles', 'bock', 'weihenstephan', 'clean', 'kolsch'],
    url: 'https://www.whclab.com/products/einstein-german-lager-dry-brewing-yeast',
    dosageGramsPerHl: { min: 100, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-apres-ski',
    lab: 'WHC Lab',
    productCode: 'Apres Ski',
    name: 'Apres Ski',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 73, max: 80 },
    temperatureC: { min: 8, max: 16 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A Swiss Alpine lager strain with a slightly more expressive profile than Einstein — a floral, faintly fruity lift over an otherwise crisp base. It works down to 8 °C, the coldest floor in the WHC range, and wants a heavier pitch than the ales do.',
    tags: ['lager', 'pilsner', 'helles', 'bock', 'crisp', 'floral', 'swiss'],
    url: 'https://www.whclab.com/products/apres-ski-swiss-lager-dry-brewing-yeast',
    dosageGramsPerHl: { min: 100, max: 200 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-cerveza-campeon',
    lab: 'WHC Lab',
    productCode: 'Cerveza Campeon',
    name: 'Cerveza Campeón',
    form: 'dry',
    type: 'lager',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 10, max: 14 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'A lager strain aimed at the Mexican-style end of the shelf: clean, crisp and very clear-finishing, with a narrow 10–14 °C working band that says how it wants to be treated. Attenuation is dependable batch to batch, which is most of the point of a house lager yeast.',
    tags: ['lager', 'mexican-lager', 'clean', 'crisp', 'clearing'],
    url: 'https://www.whclab.com/products/cerveza-campeon-mexican-lager-yeast',
    dosageGramsPerHl: { min: 100, max: 150 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-blitz-lager',
    lab: 'WHC Lab',
    productCode: 'Blitz Lager',
    name: 'Blitz Lager',
    form: 'dry',
    type: 'hybrid',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 16, max: 20 },
    flocculation: 'medium',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'Not a true lager yeast but a Saccharomyces cerevisiae selected to taste like one at ale temperatures — 16–20 °C, no glycol, no month in the tank. Malty and balanced, and it flocculates well enough to clear fast, which is the whole argument for it.',
    tags: ['lager', 'hybrid', 'kolsch', 'fast', 'crisp', 'no-chill'],
    url: 'https://www.whclab.com/products/blitz-fast-lager-dry-brewing-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },

  // -------------------------------------------------------------------------
  // Dry — session, low and non-alcoholic
  // -------------------------------------------------------------------------
  {
    id: 'whc-low-rider',
    lab: 'WHC Lab',
    productCode: 'Low Rider',
    name: 'Low Rider',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 63, max: 68 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'Deliberately low-attenuating, for table beer and session styles that need mouthfeel they cannot get from gravity. Moderate biotransformation and good glycerol production give the body; it also pushes pineapple, mango and citrus out of the dry hop.',
    tags: ['session', 'table-beer', 'low-attenuation', 'low-alcohol', 'tropical', 'full-body'],
    url: 'https://www.whclab.com/products/low-rider-low-attenuating-dry-ale-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-zero-hero',
    lab: 'WHC Lab',
    productCode: 'Zero Hero',
    name: 'Zero Hero',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 16, max: 20 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 8,
    sta1: null,
    description:
      'A maltose- and maltotriose-negative Torulaspora delbrueckii for beer under 0.5 % ABV: it barely attenuates by design, keeping body and residual sweetness. Neutral cool, fruitier warm. WHC require pasteurization, because all that sugar is still in the package. No flocculation grade is published.',
    tags: ['non-alcoholic', 'low-alcohol', 'torulaspora', 'maltose-negative', 'low-attenuation'],
    url: 'https://www.whclab.com/products/zero-hero-low-non-alcoholic-dry-yeast',
    dosageGramsPerHl: { min: 50, max: 100 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Dry — cider, champagne and seltzer
  // -------------------------------------------------------------------------
  {
    id: 'whc-big-apple',
    lab: 'WHC Lab',
    productCode: 'The Big Apple',
    name: 'The Big Apple',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 85, max: 95 },
    temperatureC: { min: 12, max: 30 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'A Saccharomyces bayanus cider strain: low sulfur, very wide temperature band and 17 % tolerance, so it finishes a high-gravity must without stalling. WHC publish no attenuation or flocculation figure — the numbers here are class-typical for a bayanus that ferments a cider to dryness and settles well.',
    tags: ['cider', 'bayanus', 'apple', 'clean', 'dry', 'high-gravity', 'perry'],
    url: 'https://www.whclab.com/products/the-big-apple',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'representative',
  },
  {
    id: 'whc-lovely-bubbly',
    lab: 'WHC Lab',
    productCode: 'Lovely Bubbly',
    name: 'Lovely Bubbly',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 12, max: 30 },
    flocculation: 'medium-low',
    alcoholTolerancePercent: 17,
    sta1: null,
    description:
      'A Saccharomyces bayanus champagne strain, clean and low in sulfur, meant to let the fruit rather than the yeast set the aroma. The 17 % tolerance and wide temperature band also make it the obvious pick for bottle-conditioning a big beer or restarting a stuck ferment.',
    tags: ['champagne', 'bayanus', 'wine', 'sparkling', 'bottle-conditioning', 'high-gravity'],
    url: 'https://www.whclab.com/products/lovely-bubbly-champagne-dry-yeast',
    dosageGramsPerHl: { min: 20, max: 40 },
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-hyperdrive',
    lab: 'WHC Lab',
    productCode: 'Hyperdrive',
    name: 'Hyperdrive',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 24, max: 32 },
    flocculation: 'medium',
    alcoholTolerancePercent: 18,
    sta1: null,
    description:
      'Yeast, nutrient and vitamin pre-blended for a sugar wash — an 18 % seltzer or RTD base in about five days, or a vodka wash. WHC publish no attenuation, so the near-total figure here is what a simple-sugar ferment implies. Their 4–8 g/L dose is far above this catalog’s g/hL ceiling, so it is omitted rather than clipped.',
    tags: ['seltzer', 'rtd', 'sugar-wash', 'neutral', 'high-gravity', 'nutrient-blend'],
    url: 'https://www.whclab.com/products/hyperdrive',
    provenance: 'representative',
  },
  {
    id: 'whc-surge-seltzer',
    lab: 'WHC Lab',
    productCode: 'Surge Seltzer',
    name: 'Surge Seltzer',
    form: 'dry',
    type: 'ale',
    attenuationPercent: { min: 90, max: 100 },
    temperatureC: { min: 25, max: 25 },
    flocculation: 'medium',
    alcoholTolerancePercent: 22,
    sta1: null,
    description:
      'A very-high-gravity strain for simple sugars, rated to 22 % and reaching 21 % in practice when paired with WHC’s matching nutrient blend. WHC give one fermentation temperature rather than a range, and publish neither attenuation nor flocculation, so those two figures are class-typical.',
    tags: ['seltzer', 'rtd', 'sugar-wash', 'high-gravity', 'neutral'],
    url: 'https://www.whclab.com/products/whc-surge-seltzer-dry-yeast',
    dosageGramsPerHl: { min: 20, max: 160 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Dry — souring bacteria
  // -------------------------------------------------------------------------
  {
    id: 'whc-lactobacillus-plantarum',
    lab: 'WHC Lab',
    productCode: 'L. plantarum',
    name: 'Lactobacillus plantarum',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 34, max: 36 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'A dried kettle-souring culture that drops pH fast and clean and leaves a citrus note behind. It is hop-intolerant, so keep the kettle bitterness at zero. It makes lactic acid, not alcohol — pitch a Saccharomyces after it, or a kveik alongside it. The attenuation figure is nominal.',
    tags: ['lactobacillus', 'kettle-sour', 'sour', 'gose', 'berliner-weisse', 'bacteria', 'citrus'],
    url: 'https://www.whclab.com/products/lactobacillus-plantarum',
    dosageGramsPerHl: { min: 10, max: 25 },
    provenance: 'representative',
  },
  {
    id: 'whc-lactobacillus-helveticus',
    lab: 'WHC Lab',
    productCode: 'L. helveticus',
    name: 'Lactobacillus helveticus',
    form: 'dry',
    type: 'wild',
    attenuationPercent: { min: 30, max: 40 },
    temperatureC: { min: 36, max: 42 },
    flocculation: 'low',
    alcoholTolerancePercent: null,
    sta1: null,
    description:
      'The sharper of WHC’s two souring bacteria: a bold, tangy citrus acidity, pitched hotter than the plantarum at 36–42 °C. Same rules apply — no kettle hops, and it acidifies rather than ferments, so the alcohol still needs a yeast. The attenuation figure is nominal.',
    tags: ['lactobacillus', 'kettle-sour', 'sour', 'tart', 'bacteria', 'berliner-weisse'],
    url: 'https://www.whclab.com/products/lactobacillus-helveticus',
    dosageGramsPerHl: { min: 10, max: 25 },
    provenance: 'representative',
  },

  // -------------------------------------------------------------------------
  // Liquid — 10 hL pitchable pouches
  // -------------------------------------------------------------------------
  {
    id: 'whc-saturated-liquid',
    lab: 'WHC Lab',
    productCode: 'Saturated',
    name: 'Saturated (liquid)',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 19, max: 22 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'The pitchable pouch of the same hazy-IPA strain WHC sell dried: stone fruit and vanilla, strong biotransformation and a creamy, under-attenuated finish. One pouch covers 10 hL up to about 1.060; double it above 1.065 and triple it above 1.080.',
    tags: ['hazy', 'neipa', 'ipa', 'stone-fruit', 'biotransformation', 'pitchable'],
    url: 'https://www.whclab.com/products/saturated-fresh-liquid-yeast-neipa-pouch',
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-haze-heaven-liquid',
    lab: 'WHC Lab',
    productCode: 'Haze Heaven',
    name: 'Haze Heaven (liquid)',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 18, max: 21 },
    flocculation: 'medium',
    alcoholTolerancePercent: 13,
    sta1: null,
    description:
      'Haze Heaven in a 10 hL pouch. Same English-derived hop-lifting strain as the dried version, but WHC rate the liquid two points higher on alcohol tolerance and a shade firmer on flocculation, so the two are kept as separate entries here.',
    tags: ['hazy', 'neipa', 'ipa', 'english-ale', 'citrus', 'tropical', 'pitchable'],
    url: 'https://www.whclab.com/products/haze-heaven',
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-vermont-haze',
    lab: 'WHC Lab',
    productCode: 'Vermont Haze',
    name: 'Vermont Haze',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 73, max: 77 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 14,
    sta1: null,
    description:
      'WHC’s take on the Conan-lineage New England strain, sold as Sanders until the rename. Pineapple, mango, orange and grapefruit over a pillowy body that comes from moderate attenuation and heavy glycerol production. Liquid only, in the 10 hL pouch.',
    tags: ['hazy', 'neipa', 'ipa', 'tropical', 'stone-fruit', 'full-body', 'pitchable'],
    url: 'https://www.whclab.com/products/vermont-haze-modern-neipa-yeast-10hl-fresh-yeast-pouch',
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-tropical-paradise',
    lab: 'WHC Lab',
    productCode: 'Tropical Paradise',
    name: 'Tropical Paradise',
    form: 'liquid',
    type: 'ale',
    attenuationPercent: { min: 63, max: 68 },
    temperatureC: { min: 18, max: 22 },
    flocculation: 'medium',
    alcoholTolerancePercent: 9,
    sta1: null,
    description:
      'A table-beer strain that cannot ferment maltotriose, so it stops in the sixties and leaves the body a low-gravity beer usually lacks. Big on glycerol and biotransformation, and it drives pineapple, mango and citrus out of the hops. Liquid only.',
    tags: ['session', 'table-beer', 'low-alcohol', 'low-attenuation', 'tropical', 'pitchable'],
    url: 'https://www.whclab.com/products/tropical-paradise-fresh-yeast-pouch',
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-helles-fire-club',
    lab: 'WHC Lab',
    productCode: 'Helles Fire Club',
    name: 'Helles Fire Club',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 70, max: 74 },
    temperatureC: { min: 10, max: 16 },
    flocculation: 'medium-high',
    alcoholTolerancePercent: 11,
    sta1: null,
    description:
      'A Munich-style lager strain that finishes crisp with a faintly sweet tail, and — unusually for a lager yeast — flocculates hard enough to crop and repitch easily. Low diacetyl and sulfur shorten conditioning, and it can be run warm for a cold IPA or a fast turnaround lager.',
    tags: ['lager', 'helles', 'munich', 'pilsner', 'bock', 'clean', 'pitchable'],
    url: 'https://www.whclab.com/products/helles-fire-club-premium-fresh-liquid-lager-yeast',
    provenance: 'spec-sheet',
  },
  {
    id: 'whc-einstein-liquid',
    lab: 'WHC Lab',
    productCode: 'Einstein',
    name: 'Einstein (liquid)',
    form: 'liquid',
    type: 'lager',
    attenuationPercent: { min: 76, max: 80 },
    temperatureC: { min: 9, max: 16 },
    flocculation: 'low',
    alcoholTolerancePercent: 12,
    sta1: null,
    description:
      'The Weihenstephan-origin lager strain in a 10 hL pitchable pouch rather than a sachet. Cold, clean and crisp across pilsner, helles and bock; low flocculation means the beer wants lagering time or filtration before it goes bright.',
    tags: ['lager', 'pilsner', 'helles', 'bock', 'weihenstephan', 'clean', 'pitchable'],
    url: 'https://www.whclab.com/products/einstein-german-lager-liquid-yeast',
    provenance: 'spec-sheet',
  },
];
